import { makeAutoObservable, observable } from "mobx";

export interface LiveUpdatesHandlers {
  onQuotesUpdated(): void;
  onPortfolioRecalculated(portfolioId: number): void;
  onReconnected(): void;
}

export interface LiveUpdatesConnection {
  start(): Promise<void>;
  stop(): Promise<void>;
}

export type LiveUpdatesConnectionFactory = (handlers: LiveUpdatesHandlers) => LiveUpdatesConnection;

/**
 * Server events of the signed-in session as revisions. A screen reads the revision that covers its data and reloads
 * whenever it changes. A reconnect raises every revision, because events may have been missed while disconnected.
 */
export class LiveUpdatesStore {
  private quotesUpdateCount = 0;
  private recalculationCount = 0;
  private reconnectCount = 0;
  private readonly portfolioRecalculationCounts = observable.map<number, number>();
  private readonly createConnection: LiveUpdatesConnectionFactory;
  private connection: LiveUpdatesConnection | null = null;

  public constructor(createConnection: LiveUpdatesConnectionFactory) {
    this.createConnection = createConnection;
    makeAutoObservable<LiveUpdatesStore, "createConnection" | "connection">(this, { createConnection: false, connection: false });
  }

  /** Changes after new quotes: prices and market values of every portfolio, instrument and watchlist item. */
  public get quotesRevision(): number {
    const quotesRevision = this.quotesUpdateCount + this.reconnectCount;

    return quotesRevision;
  }

  /** Changes after new quotes or a recalculation of any portfolio: totals over all portfolios. */
  public get valuesRevision(): number {
    const valuesRevision = this.quotesRevision + this.recalculationCount;

    return valuesRevision;
  }

  /** Changes after a recalculation of the portfolio: its derived data (positions, lots, snapshots, pending flag). */
  public getPortfolioRecalculationRevision(portfolioId: number): number {
    const recalculationRevision = (this.portfolioRecalculationCounts.get(portfolioId) ?? 0) + this.reconnectCount;

    return recalculationRevision;
  }

  /** Changes after new quotes or a recalculation of the portfolio: its market values. */
  public getPortfolioValuesRevision(portfolioId: number): number {
    const portfolioValuesRevision = this.quotesUpdateCount + this.getPortfolioRecalculationRevision(portfolioId);

    return portfolioValuesRevision;
  }

  public connect(): void {
    if (this.connection !== null) {
      return;
    }

    const connection = this.createConnection({
      onQuotesUpdated: () => this.markQuotesUpdated(),
      onPortfolioRecalculated: (portfolioId) => this.markPortfolioRecalculated(portfolioId),
      onReconnected: () => this.markReconnected(),
    });
    this.connection = connection;
    void connection.start();
  }

  public disconnect(): void {
    const connection = this.connection;
    this.connection = null;
    void connection?.stop();
  }

  private markQuotesUpdated(): void {
    this.quotesUpdateCount += 1;
  }

  private markPortfolioRecalculated(portfolioId: number): void {
    this.recalculationCount += 1;
    this.portfolioRecalculationCounts.set(portfolioId, (this.portfolioRecalculationCounts.get(portfolioId) ?? 0) + 1);
  }

  private markReconnected(): void {
    this.reconnectCount += 1;
  }
}
